import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, TrendingUp, BarChart, PieChart } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function AnalyticsDashboardPage() {
  return (
    <>
      <Helmet>
        <title>Analytics Dashboard - Track Your Job Search Performance | LoopBaan.ai</title>
        <meta name="description" content="Visualize your job search with powerful analytics. Track response rates, interview success, and optimize your strategy." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">DATA-DRIVEN</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Track Every Metric That{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Matters
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Visualize your job search performance with powerful analytics. See what's working and optimize your strategy.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    View Analytics <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: TrendingUp,
                  title: "Response Rates",
                  description: "Track how many applications turn into interviews. Identify which roles and industries respond best."
                },
                {
                  icon: BarChart,
                  title: "Interview Success",
                  description: "Analyze your interview performance and see how many interviews convert to offers."
                },
                {
                  icon: PieChart,
                  title: "Application Breakdown",
                  description: "Visualize where you're applying: by industry, company size, location, and salary range."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">Key Metrics</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Applications submitted over time",
                  "Response rate percentage",
                  "Interview conversion rate",
                  "Time from application to offer",
                  "Most successful job titles",
                  "Best performing industries",
                  "Optimal application timing",
                  "Resume effectiveness score"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Optimize Your Job Search Strategy
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Use data to make better decisions and land your dream job faster.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Start Tracking <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
